import Vuex from 'vuex';

import Domain from 'helpers/Domain';
import ProfileStore from 'apps/userProfile/stores/modules/ProfileStore';
import FlowStore from 'aa/vue/stores/modules/FlowStore';
import PaymentStore from 'aa/vue/stores/modules/PaymentStore';
import TrackingStore from 'aa/vue/stores/modules/TrackingStore';
import PlanStore from 'aa/vue/stores/modules/PlanStore';
import PageAttributesStore from 'aa/vue/stores/modules/PageAttributesStore';
import ShowsPickerStore from 'apps/showsPicker/stores/ShowsPickerStore';
import { AA_PURCHASE } from 'helpers/featureConstants';
import UserStore from 'aa/vue/stores/modules/UserStore';
import ModalStore from 'aa/vue/stores/modules/ModalStore';
import FeatureFlagsStore from "cbs-core-ui/src/js/apps/featureFlags/stores/FeatureFlagsStore";
// CBSCOMSUB-4696
import NotificationBarStore from 'aa/vue/stores/modules/NotificationBarStore';
import PaymentService from 'aa/vue/services/PaymentService';
import PartnerStore from './modules/Partner/PartnerStore';
import { loadAuthSuiteStore } from 'services/AuthSuite/helpers';
import {HOME_URL} from "aa/vue/constants/aaRoutes";

export default function () {
    return new Vuex.Store({
        state: {
            count: 0,
            token: null,
            promo: null,
            navFrom: null,
            imageHost: null,
            featureFlags: {},
            localeDictionary: {},
            userMessage: {},
            serverData: {}, // does this need to be here?
        },

        mutations: {
            setToken(state, token) {
                state.token = token;
            },
            setPromo(state, promo) {
                state.promo = promo;
            },
            setNavFrom(state, from) {
                state.navFrom = from;
            },
            setImageHost(state, imageHost) {
                state.imageHost = imageHost;
            },
            setFeatureFlags(state, featureFlags) {
                if (featureFlags && typeof featureFlags === 'object') {
                    state.featureFlags = featureFlags;
                }
            },
            setLocaleDictionary(state, localeDictionary) {
                state.localeDictionary = localeDictionary;
            },
            setServerData(state, payload) {
                state.serverData = payload;
            },
            setUserMessage(state, payload) {
                state.userMessage = payload;
            },
        },

        getters: {
            getLocaleDictionary: (state) => state.localeDictionary,

            cmsImageBaseURL: (state) => {
                return state.imageHost + '/base/';
            },

            // CBSCOMSUB-4696
            getServerDataByKey: (state) => (key) => {
                if (!state.serverData) return null;

                if (key in state.serverData) {
                    return state.serverData[key];
                }

                return null;
            },

            featureIsActive: (state) => (feature) => {
                return state.featureFlags[feature] === true;
            },

            canBundleShowtime: (state, getters) => {
                return Boolean(
                    getters.featureIsActive('showtime_bundle') &&
                        !getters['flow/isEdu']
                );
            },

            /**
             * @param state
             * @param getters
             * @returns {*|boolean}
             */
            bundleShowtime: (state, getters) => {
                return Boolean(
                    getters.canBundleShowtime && state.plan.addonShowtime
                );
            },
            getProductParamsForTracking: (state, getters) => {
                let plan = getters['plan/getCurrentPlan'];

                if (getters['featureIsActive'](AA_PURCHASE) && plan) {
                    let trackingParams = {
                        purchaseProductName: plan.titleForTracking,
                        productOfferPeriod: plan.trialString,
                        productPricingPlan: plan.planType,
                        purchasePrice: plan.rawPrice,
                        partnerBundle: getters.bundleShowtime ? 'showtime' : ''
                    };

                    return trackingParams;
                }

                return {};
            },

            getPaymentPageProductParamsForTracking: (state, getters) => {
                let plan = getters['plan/getCurrentPlan'];

                if (getters['featureIsActive'](AA_PURCHASE) && plan) {
                    let trackingParamsPaymentPage = {
                        purchaseEventBillingStart: '1',
                        purchaseProduct: plan.recurlyCode,
                        purchaseQuantity: '1',
                        purchaseCategory: plan.titleForTracking
                    };

                    return Object.assign(
                        getters.getProductParamsForTracking,
                        trackingParamsPaymentPage
                    );
                }
                return {};
            },

            getRedirectUrlForResellers: (state, getters) => (userState) => {
                let userData = (userState) ? userState : state.user;
                let source = userData.svod.user_package.source,
                    status = userData.svod.user_package.status,
                    isCancelled = (status === 'ACTIVE_CANCELLED'),
                    partner = getters['flow/type'],
                    partnersWithMessage = ['walmartplus'];
               
                if (partnersWithMessage.includes(partner) && (source !== partner)) {
                    return (isCancelled) ? '/account/' :
                        '/account/?notification=partner-subscribed&partner=' + partner + '';
                } else {
                    return `${CBS.Registry.region.prefix || ''}${HOME_URL}`;
                }
            }
        },

        actions: {
            loadServerData({ dispatch, commit }, json) {
                if (json.flow) {
                    dispatch('flow/loadStore', json.flow);
                }

                dispatch(
                    'pageAttributes/setPageAttributes',
                    json.pageAttributes || {}
                );

                // plan type - force annual to show up first if flow says so
                dispatch('plan/setPlanType', {
                    planType:
                        json && json.flow && json.flow.forceAnnual
                            ? 'annual'
                            : 'monthly'
                });

                if (json.plans) {
                    dispatch('plan/setPlans', json.plans);
                }

                commit('setFeatureFlags', json.featureFlag);
                commit('user/parseUserData', json.user);
                if (Domain.isInternational()) {
                    commit('setServerData', json);

                    // load AuthSuite store, the feature checking is in this function
                    loadAuthSuiteStore(this, {
                        ...json.authSuiteConfig,
                        regionBaseUrl: json.regionBaseUrl
                    });
                }

                if (json.user?.isLoggedIn) {
                    dispatch('flow/didCompleteSignIn');
                }

                if (json.featureFlag?.aa_purchase) {
                    dispatch('plan/loadStore', {
                        planType: json?.pageAttributes?.default_to_annual
                            ? 'annual'
                            : 'monthly',
                        plans: json.plans
                    });
                }

                if (json?.recurlyPublicKey) {
                    dispatch('payment/loadStore', {
                        recurlyPublicKey: json.recurlyPublicKey
                    });
                }
            },
            getPromo({ commit, rootState }) {
                return new Promise((resolve, reject) => {
                    PaymentService.getPromo(rootState.user.statusCode)
                        .then((promo) => {
                            commit('setPromo', promo);

                            resolve();
                        })
                        .catch((err) => {
                            reject(err);
                        });
                });
            },

            async updateCoupon({ commit }, couponCode) {
                if (typeof couponCode === 'string') {
                    couponCode = couponCode.trim();
                }

                commit('setPromo', couponCode);
            }
        },

        // namespaces defined here
        modules: {
            user: UserStore,
            profile: ProfileStore,
            payment: PaymentStore,
            tracking: TrackingStore,
            flow: FlowStore,
            pageAttributes: PageAttributesStore,
            plan: PlanStore,
            showspicker: ShowsPickerStore,
            partner: PartnerStore,
            
            // CBSCOMSUB-4696
            notification: NotificationBarStore,
            modal: ModalStore,
            featureFlags: FeatureFlagsStore,
        }
    });
}
